// Single source of truth for gating unreleased features.
//
// VIDEO_RECORDER_ENABLED — controls the entire gift-recorder + gift-message
// feature set. Until product / legal approval to ship, this MUST stay `false`.
// Flipping it to `true` is the only change required to expose the feature
// across the whole app.
//
// When `false`:
//   • Sender self-record prompt + slot-based recorder (gift-flow step 1) — hidden
//   • Navigation never lands on step1SubStep 3 (prompt) or 4 (recorder)
//   • Contributor "Tell Your Story & Send" tile (gift-flow step 3 upload) — hidden
//   • Contributor occasion picker + recorder modal — hidden
//   • Receiver-side "Tell Your Story" Yes/No prompt screen (gift-content) — skipped
//   • Receiver-side recorder + occasion picker (gift-content) — hidden
//   • GiftMessagePlayer popup (the "play your gift" overlay on gift-content) — hidden
//   • Per-folder "🎁 Open Gift" pills on gift-content folder cards — hidden
//   • Phone-as-webcam (/mobile-camera/:sessionId) — renders an unavailable view
//
// When `true`: everything above is restored to its normal behaviour.
//
// Do NOT add additional conditions for hiding the feature in other places
// without also gating them on this flag — the goal is one switch to rule
// them all so re-enabling is a one-line change.
export const VIDEO_RECORDER_ENABLED = true;
export const VIDEO_RECORDER_COMMENTARY_ENABLED = false;

// GUEST_INVITE_ENABLED — gates the live "Add guest" / meeting feature inside
// the gift recorder, where the host invites a remote person to join the
// recording session over WebRTC (shareable /interview/join link, PIP/side-by-
// side guest tile, active-speaker ring).
//
// When `false`:
//   • The "Add guest" button + connected-guest badge on the recorder stage — hidden
//   • The guest invite-link popup — hidden
//   • The remote-guest PIP overlay — hidden
// The host can still record solo exactly as before; nothing else changes.
//
// This does NOT control the standalone /interview/* pages (intro/host/review),
// which are a separate entry point.
export const GUEST_INVITE_ENABLED = true;

// SHARED_FEATURE_ENABLED — gates the "shared folders" feature end-to-end, i.e.
// the flow where a user creates a shareable link so others can contribute media
// into a shared folder. This flag ONLY controls the entry points into the
// feature; flip it to expose or hide them everywhere in one place.
//
// When `false`:
//   • The "Shared Folders" item in the upload menu (UploadDropdown) — hidden
//   • The "Shared" tab on the /your-videos page — hidden (and if a stale
//     sessionStorage value had it selected, the page falls back to Videos)
//
// When `true`: both entry points are restored to their normal behaviour.
//
// This value is now driven by the backend: getMyProfile returns
// `features.share_folders`, and app.jsx calls setSharedFeatureEnabled() on load
// to flip this. It is an ES-module live binding, so every `import
// { SHARED_FEATURE_ENABLED }` reads the current value on each render — no
// consumer changes are needed. The initial value is seeded from a localStorage
// cache so the correct state is available on reload before the profile call
// resolves (avoids UI flicker). The mutable export is intentional — it is the
// ES-module live binding that lets consumers pick up the backend value without
// any changes, so the lint rule is disabled here on purpose.
// eslint-disable-next-line import/no-mutable-exports
export let SHARED_FEATURE_ENABLED = (() => {
  try {
    return localStorage.getItem('feature_share_folders') === '1';
  } catch (e) {
    return false;
  }
})();

// Called once from app.jsx after getMyProfile resolves. Accepts bool / '1' / 1.
export const setSharedFeatureEnabled = (value) => {
  SHARED_FEATURE_ENABLED = value === true || value === '1' || value === 1;
  try {
    localStorage.setItem('feature_share_folders', SHARED_FEATURE_ENABLED ? '1' : '0');
  } catch (e) {
    // localStorage unavailable — in-memory value still applies for this session
  }
};

// ONBOARDING_CHECKLIST_ENABLED — gates the "Getting Started" banner on /access,
// the projector picker every user lands on after logging in or signing up
// (Upload, Invite User, Rearrange, Tell Your Story, Add Card). The banner
// already retires itself once every task is done — and the user can close it
// with its X — so this flag is only for switching the whole feature off.
//
// When `false`:
//   • The banner never renders on /access
//
// When `true`: it renders for users with at least one outstanding task.
//
// Progress is still recorded while the flag is off (Utils/onboardingChecklist),
// so flipping it back on shows the user's real state rather than a blank slate.
export const ONBOARDING_CHECKLIST_ENABLED = true;

// GIFT_PLAN_CARDS_MOBILE_VERTICAL — controls how the gift plan cards are laid
// out on mobile (≤900px) in the payment step.
//   • true  → cards stack vertically, one below the other (no swipe carousel)
//   • false → original horizontal swipe carousel with dots
// Desktop is unaffected either way. Flip this one line to switch layouts.
export const GIFT_PLAN_CARDS_MOBILE_VERTICAL = true;

// THUMBNAIL_TILE_STYLE — how video thumbnail tiles are shaped wherever
// VideoAutoPlayer renders in a row (the /profile Categories, Playlists and
// Resume Watching rows). Managed at runtime from the Preferences modal on
// /content-layout (Projector Setting tab) via localStorage; consumers read it
// with getThumbnailTileStyle() at render time, so it applies on the next
// /profile render (a reload guarantees it everywhere).
//   'letterbox' → every tile is a fixed 16:9 box; portrait sources are shown
//                 with object-fit: contain over a blurred backdrop copy
//                 (the original behaviour).
//   'adaptive'  → every tile in a row shares one fixed HEIGHT; the width
//                 follows the thumbnail's own aspect ratio (portrait = narrow,
//                 landscape = wide), object-fit: cover, no letterbox/backdrop.
//   'poster'    → every tile a uniform portrait poster card (2:3), thumbnail
//                 cover-cropped to fill (TV-app / streaming-poster look).
//   'framed'    → a wide horizontal card: a small thumbnail showing the WHOLE
//                 image (contain) on a solid backdrop, with the title/metadata
//                 beside it. Nothing cropped; portrait reads as intentional.
//   'cinematic' → adaptive-sized tiles with the title overlaid on the thumbnail
//                 over a bottom gradient (no separate title below).
export const THUMBNAIL_TILE_STYLE_KEY = 'thumbnail_tile_style';
export const DEFAULT_THUMBNAIL_TILE_STYLE = 'letterbox';
export const THUMBNAIL_TILE_STYLES = ['letterbox', 'adaptive', 'poster', 'framed', 'cinematic'];

export const getThumbnailTileStyle = () => {
  try {
    const v = localStorage.getItem(THUMBNAIL_TILE_STYLE_KEY);
    return THUMBNAIL_TILE_STYLES.includes(v) ? v : DEFAULT_THUMBNAIL_TILE_STYLE;
  } catch (e) {
    return DEFAULT_THUMBNAIL_TILE_STYLE;
  }
};

export const setThumbnailTileStyle = (v) => {
  try {
    localStorage.setItem(THUMBNAIL_TILE_STYLE_KEY, THUMBNAIL_TILE_STYLES.includes(v) ? v : DEFAULT_THUMBNAIL_TILE_STYLE);
  } catch (e) {
    // localStorage unavailable — selection won't persist this session
  }
};

// PLAYER_UI_STYLE — which chrome the video player wears. Managed at runtime
// from the Preferences modal on /content-layout (Projector Setting tab) and
// from the gear button inside the player itself; stored in localStorage only
// (no account sync), exactly like THUMBNAIL_TILE_STYLE above.
//
// Every skin drives the SAME controls component (PLayerControls.jsx), which
// builds one set of pieces — back, title, transport, volume, seek bar — and
// arranges them into a different LAYOUT per skin. Playback, seeking, the
// hover sprite preview and the next-video card behave identically in all of
// them.
//   'netflix'  → the original: gradient scrims, seek bar over a single
//                left-packed transport row (default).
//   'minimal'  → no bars at all: a white pill play button with the title and
//                time beside it, aux icons far right, and a hairline seek
//                welded to the bottom edge. Skip buttons dropped.
//   'glass'    → one floating capsule dock inset from the edges, with the
//                seek bar INLINE between its two time readouts.
//   'classic'  → desktop-app console: opaque title strip and bottom panel,
//                seek row flanked by times over a three-column button row
//                (volume left, transport centred, fullscreen right).
//   'theater'  → cinema chrome: solid letterbox bars, centred marquee title,
//                transport lifted off the bar into a console floating over
//                the picture; the bar carries only the seek line.
//   'neon'     → game-HUD: lit transport cluster bottom-left, readout chip
//                centred, aux cluster bottom-right, glowing seek along the
//                very bottom edge — no panel behind any of it.
//   'studio'   → wide scrim bar: always-open volume slider far left, the
//                transport centred and flanked by the elapsed / total times
//                around a ring play button, gear + fullscreen far right.
//   'capsule'  → two rounded pill groups under a full-width hairline seek
//                with a bar-shaped handle: play/skip/time on the left, and
//                volume / playback speed / gear / fullscreen on the right.
//   'vision'   → one wide frosted bar floating clear of the bottom edge:
//                transport on the left, a now-playing card in the middle
//                (thumbnail, title, time, and the seek line along its foot),
//                speed / volume / fullscreen on the right.
export const PLAYER_UI_STYLE_KEY = 'player_ui_style';
export const DEFAULT_PLAYER_UI_STYLE = 'netflix';
export const PLAYER_UI_STYLES = [
  'netflix', 'minimal', 'glass', 'classic', 'theater', 'neon', 'studio', 'capsule', 'vision',
];
// Labels + one-liners, shared by the /content-layout card and the in-player
// menu so both lists always read the same.
export const PLAYER_UI_STYLE_OPTIONS = [
  { key: 'netflix', label: 'Netflix', desc: 'Scrims, one control row' },
  { key: 'minimal', label: 'Minimal', desc: 'No bars, hairline seek' },
  { key: 'glass', label: 'Glass', desc: 'One floating dock' },
  { key: 'classic', label: 'Classic', desc: 'Desktop console' },
  { key: 'theater', label: 'Theater', desc: 'Letterbox bars' },
  { key: 'neon', label: 'Neon', desc: 'Lit HUD clusters' },
  { key: 'studio', label: 'Studio', desc: 'Ring play, centred' },
  { key: 'capsule', label: 'Capsule', desc: 'Two pill groups' },
  { key: 'vision', label: 'Vision', desc: 'Now-playing card' },
];
// Fired on the window whenever the style changes, so an open player restyles
// itself immediately instead of waiting for a reload.
export const PLAYER_UI_STYLE_EVENT = 'player-ui-style-change';

export const getPlayerUiStyle = () => {
  try {
    const v = localStorage.getItem(PLAYER_UI_STYLE_KEY);
    return PLAYER_UI_STYLES.includes(v) ? v : DEFAULT_PLAYER_UI_STYLE;
  } catch (e) {
    return DEFAULT_PLAYER_UI_STYLE;
  }
};

export const setPlayerUiStyle = (v) => {
  const next = PLAYER_UI_STYLES.includes(v) ? v : DEFAULT_PLAYER_UI_STYLE;
  try {
    localStorage.setItem(PLAYER_UI_STYLE_KEY, next);
  } catch (e) {
    // localStorage unavailable — selection won't persist this session
  }
  try {
    window.dispatchEvent(new CustomEvent(PLAYER_UI_STYLE_EVENT, { detail: next }));
  } catch (e) {
    // CustomEvent unsupported — the next mount still reads the stored value
  }
  return next;
};
